import React, { useEffect, useRef, useState } from 'react';
import { postAPI, formatDate, convertTo12Hour } from '../../app/api';
import { io } from 'socket.io-client';

const FaceComponent = ({ public_access_token, org_id, user_unique_id, setIsSafeCameraDistance, safeCameraDistanceTotalRef, setIsFaceVerified, isFaceVerified, isFaceVerifiedRef, exam_id }) => {
  const webcamRef = useRef(null);
  const [userId] = useState(user_unique_id);
  const [statusText, setStatusText] = useState('');
  const [recordedVideoHTML, setRecordedVideoHTML] = useState('');
  const distanceCheckInterval = useRef(null); // hold interval ref
  const socketRef = useRef(null);
  const lastSessionIdRef = useRef(null);
  const segmentCounterRef = useRef(0);
  const recorderRef = useRef(null);

  useEffect(() => {
    initCamera();
    // init socket
    socketRef.current = io('https://face.stremaxscholar.com');
    socketRef.current.on('connect', () => {
      setStatusText('Socket connected');
      // Resume: ask the server what segment index it has on disk for this session.
      // This handles reconnect after network drop, laptop sleep, or page refresh mid-exam.
      const sid = lastSessionIdRef.current || (exam_id ? String(exam_id) : String(userId));
      if (sid) {
        fetch(
          `https://face.stremaxscholar.com/session-status?my_id=${encodeURIComponent(userId)}&session_id=${encodeURIComponent(sid)}`
        )
          .then(r => r.json())
          .then(data => {
            if (data.status === 'ok' && data.next_segment > segmentCounterRef.current) {
              segmentCounterRef.current = data.next_segment;
              setStatusText(`Resumed from segment ${data.next_segment}`);
              console.log(`[RESUME] Server has up to seg ${data.last_segment}, resuming from ${data.next_segment}`);
            }
          })
          .catch(() => { });
      }
    });
    socketRef.current.on('chunk_ack', (d) => {
      setStatusText(`Uploaded segment ${d.segment}`);
    });
    socketRef.current.on('finalize_ack', (d) => {
      setStatusText('Finalize scheduled');
      // show live URL (Bunny predictable path)
      const sessionId = window.__attendance_session_id || '';
      if (sessionId) {
        const liveUrl = `https://fsexm2.b-cdn.net/recordings/${userId}/${sessionId}/live.mp4`;
        setRecordedVideoHTML(`<video width="200" height="150" controls autoplay><source src="${liveUrl}" type="video/mp4">Your browser does not support the video tag.</video>`);
      }
    });
    socketRef.current.on('error', (e) => setStatusText(e && e.message ? e.message : 'Socket error'));

    // ---- Browser close / tab close / internet disconnect safety ----
    const sendFinalizeBeacon = () => {
      const sid = lastSessionIdRef.current || window.__attendance_session_id;
      if (!sid) return;
      const payload = JSON.stringify({
        my_id: userId,
        session_id: sid,
        total_segments: segmentCounterRef.current
      });
      try {
        navigator.sendBeacon(
          'https://face.stremaxscholar.com/finalize-beacon',
          new Blob([payload], { type: 'application/json' })
        );
      } catch (e) {
        // fallback: try fetch keepalive
        try {
          fetch('https://face.stremaxscholar.com/finalize-beacon', {
            method: 'POST',
            body: payload,
            headers: { 'Content-Type': 'application/json' },
            keepalive: true
          }).catch(() => { });
        } catch (_) { }
      }
    };

    const handleBeforeUnload = () => {
      sendFinalizeBeacon();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendFinalizeBeacon();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // remove browser close handlers
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // clear interval on unmount
      if (distanceCheckInterval.current) clearInterval(distanceCheckInterval.current);
      // Stop continuous recorder before finalizing
      try {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          recorderRef.current.stop();
        }
      } catch (e) { }
      // finalize recording session before disconnecting
      sendFinalizeBeacon(); // beacon as primary (survives unmount)
      try {
        if (socketRef.current && socketRef.current.connected && lastSessionIdRef.current) {
          socketRef.current.emit('finalize_stream', {
            my_id: userId,
            session_id: lastSessionIdRef.current,
            total_segments: segmentCounterRef.current
          });
        }
      } catch (e) { }
      // brief delay so finalize emit sends before disconnect
      setTimeout(() => {
        try {
          if (socketRef.current) socketRef.current.disconnect();
        } catch (e) { }
      }, 500);
    };
  }, []);

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = webcamRef.current;
      video.srcObject = stream;
      await new Promise(resolve => (video.onloadedmetadata = resolve));

      setTimeout(() => {
        verifyFace();
      }, 2000);
      // Start continuous recording — one session that runs until component unmount
      startContinuousRecording();
    } catch (err) {
      setStatusText("Cannot access webcam: " + err.message);
    }
  };

  const captureBlobFromWebcam = async () => {
    const video = webcamRef.current;
    await new Promise(resolve => {
      const checkReady = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) resolve();
        else setTimeout(checkReady, 100);
      };
      checkReady();
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg'));
    if (!blob) throw new Error("Failed to capture image from webcam.");
    return blob;
  };

  const sendFormData = async (url, blob, key = 'image') => {
    const formData = new FormData();
    formData.append('my_id', userId);
    formData.append(key, blob, 'upload.jpg');

    const res = await fetch(url, { method: 'POST', body: formData });
    const json = await res.json();

    // Fix possible NumPy bool
    if (typeof json.match === 'object' && 'valueOf' in json.match) {
      json.match = Boolean(json.match.valueOf());
    }

    return json;
  };

  const verifyFace = async () => {
    if (!userId) return alert("Please enter your ID.");
    try {
      const blob = await captureBlobFromWebcam();
      const data = await sendFormData(`https://face.stremaxscholar.com/face-verify`, blob);

      if (data.status === 'success' && data.match) {
        isFaceVerifiedRef.current = 1;
        setIsFaceVerified(data.match ? true : false);
        startDistanceCameraCheck(); // start interval after success
      } else {
        setIsFaceVerified(false);
        setTimeout(() => {
          verifyFace(); // retry after 2 sec if not verified
        }, 5000);
      }

      // console.log("VerifyFace Response: ", data);
    } catch (err) {
      setTimeout(() => {
        verifyFace(); // retry after 2 sec if not verified
      }, 5000);
      console.error("VerifyFace Error: ", err.message);
    }
  };

  const isSafeCameraDistance = async () => {
    if (!userId) return alert("Please enter your ID.");
    try {
      const blob = await captureBlobFromWebcam();
      const data = await sendFormData(`https://face.stremaxscholar.com/is-safe-camera-distance`, blob);

      // Set safe distance true/false
      setIsSafeCameraDistance(data.warnings === 0 ? true : false);

      // Update ref
      safeCameraDistanceTotalRef.current = safeCameraDistanceTotalRef.current + parseInt(data.warnings);
      // console.log("DistanceCheck Warnings:", data.warnings, "Total:", safeCameraDistanceTotalRef.current);

    } catch (err) {
      console.log(err.message);
    }
  };




  const startDistanceCameraCheck = () => {
    if (distanceCheckInterval.current) return; // already running

    distanceCheckInterval.current = setInterval(() => {
      isSafeCameraDistance();
    }, 1000);


    // Start recursive attention check
    // runAttentionCheckLoop();
    // setInterval(() => {
    //   startAttentionCheck(20 * 1000); // wait for it to finish
    // }, 20 * 1000);
    console.log("Started distance check interval.");
  };



  // const runAttentionCheckLoop = async () => {
  //   setTimeout(() => {
  //     runAttentionCheckLoop(); // repeat after 5 sec
  //   }, 20000); // wait gap of 5 sec — adjust if needed
  // };

  // Upload a single chunk via socket (with HTTP fallback)
  const uploadChunk = async (blob, idx) => {
    const sessionId = lastSessionIdRef.current;
    if (!socketRef.current || !socketRef.current.connected) {
      // fallback to HTTP if socket not ready
      const fd = new FormData();
      fd.append('my_id', userId);
      fd.append('session_id', sessionId);
      fd.append('segment', String(idx));
      fd.append('video', blob, `${sessionId}_seg${String(idx).padStart(5, '0')}.webm`);
      try {
        const res = await fetch('https://face.stremaxscholar.com/face-attention-check', { method: 'POST', body: fd });
        const data = await res.json().catch(() => ({}));
        if (data && data.status) setStatusText(data.message || 'Chunk uploaded');
        return data;
      } catch (err) {
        console.error('HTTP fallback failed', err);
        return null;
      }
    }

    // use socket acknowledgement callback and retry on failure
    const maxRetries = 3;
    let attempt = 0;
    window.__attendance_session_id = sessionId;

    const tryEmit = () => new Promise((resolve) => {
      attempt++;
      try {
        socketRef.current.emit('video_chunk', { my_id: userId, session_id: sessionId, segment: idx }, blob, (resp) => {
          if (resp && resp.status === 'ok') {
            setStatusText(`Uploaded segment ${resp.segment}`);
            resolve({ status: 'ok', segment: resp.segment, url: resp.url });
          } else {
            if (attempt < maxRetries) {
              setTimeout(() => resolve(tryEmit()), 500);
            } else {
              setStatusText('Upload failed after retries');
              resolve(null);
            }
          }
        });
      } catch (e) {
        if (attempt < maxRetries) {
          setTimeout(() => resolve(tryEmit()), 500);
        } else {
          console.error('socket emit failed', e);
          resolve(null);
        }
      }
    });

    return tryEmit();
  };

  // Start ONE continuous recording session that runs until component unmount.
  // Each 2s chunk is uploaded immediately. No stopping/restarting.
  // Finalize (merge into single mp4) only happens on unmount or browser close.
  const startContinuousRecording = () => {
    const stream = webcamRef.current?.srcObject;
    if (!stream) {
      setStatusText('No webcam stream available');
      return;
    }

    const timeslice = 2000; // 2s chunks
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType });
    const sessionId = exam_id ? String(exam_id) : String(userId);
    lastSessionIdRef.current = sessionId;
    window.__attendance_session_id = sessionId;

    recorder.ondataavailable = async (e) => {
      if (!e.data || e.data.size === 0) return;
      const idx = segmentCounterRef.current++;
      uploadChunk(e.data, idx).catch(console.error);
    };

    recorder.onerror = (ev) => {
      console.error('MediaRecorder error', ev);
    };

    // start recording — runs indefinitely in timeslice mode
    recorder.start(timeslice);
    recorderRef.current = recorder;
    setStatusText(`Recording started (session ${sessionId})`);
    console.log(`Continuous recording started: session=${sessionId}, timeslice=${timeslice}ms`);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial' }}>
      <div className='faceRecognation-container'>
        <div className='faceRecognation'>
          <div className='overlay'></div>
          <video ref={webcamRef} width="150" height="150" autoPlay muted playsInline></video>
        </div>
      </div>
      <div>
        {/* <button onClick={startAttentionCheck} className='d-none'>Start Attention Check</button> */}
      </div>
    </div>
  );
};

export default FaceComponent;





